import { useMemo, useEffect } from 'react';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { RateType, useOfferBuilderContext } from '../OfferBuilderContext';
import { getNameYourRateMarkets } from '../utils';
import PricingStrategyOptionsBase from './PricingStrategyOptionsBase';

/**
 * Determines the default rate type based on existing rates in name your rate markets.
 * Returns the rate type that appears most frequently. In case of tie or no rates, defaults to APR.
 */
const getDefaultRateTypeFromExistingRates = (markets: TakerMarket[]): RateType => {
  const rateCounts = { APR: 0, DISC: 0 };

  markets.forEach((market) => {
    const hasApr = market.offerConfig.maxApr !== undefined && market.offerConfig.maxApr !== null;
    const hasDiscount = market.offerConfig.maxDiscount !== undefined && market.offerConfig.maxDiscount !== null;

    // Determine which rate type this market has based on isDiscountBidding flag
    if (hasApr || hasDiscount) {
      const marketRateType = market.offerConfig.isDiscountBidding ? 'DISC' : 'APR';
      rateCounts[marketRateType]++;
    }
  });

  // Return the rate type with the highest count, defaulting to APR in case of tie or no rates
  return rateCounts.DISC > rateCounts.APR ? 'DISC' : 'APR';
};

const PricingStrategyOptionsNoPricing = () => {
  const { dispatch, state } = useOfferBuilderContext();
  const { rateType, selected } = state;
  const nameYourRateMarkets = useMemo(() => getNameYourRateMarkets(selected), [selected]);

  // Set rate type based on existing rates when pricing is disabled
  useEffect(() => {
    if (!rateType) {
      const defaultRateType = getDefaultRateTypeFromExistingRates(nameYourRateMarkets);
      dispatch({
        type: 'SET_RATE_TYPE',
        payload: defaultRateType,
      });
    }
  }, [rateType, nameYourRateMarkets, dispatch]);

  const onRateTypeChange = (value: RateType) => {
    dispatch({
      type: 'CHANGE_RATE_TYPE_AND_CLEAR_STRATEGY',
      payload: {
        rateType: value,
        nameYourRateMarkets: getNameYourRateMarkets(selected),
      },
    });
  };

  return <PricingStrategyOptionsBase pricingData={null} onRateTypeChange={onRateTypeChange} />;
};

export default PricingStrategyOptionsNoPricing;
